import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../utils/tpoGenQueryKeys';
import type { PickerPlan, UniversePlansResponse } from '../../types';

const fetchUniversePlans = async (
    dim1Labels: string[],
    dim2Labels: string[],
    search?: string,
    exclude?: string[],
    signal?: AbortSignal,
): Promise<UniversePlansResponse> => {
    let token = '';
    try { token = localStorage.getItem('accessToken') ?? ''; } catch {}

    const params = new URLSearchParams();
    for (const d of dim1Labels) params.append('dim1', d);
    for (const d of dim2Labels) params.append('dim2', d);
    if (search) params.append('search', search);
    if (exclude) {
        for (const id of exclude) params.append('exclude', id);
    }

    const response = await axios(`${tpoGenUrls.getUniversePlans}?${params.toString()}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        signal,
    });
    return response.data;
};

interface UseUniversePlansArgs {
    dim1Labels: string[];
    dim2Labels: string[];
    search?: string;
    exclude?: string[];
    enabled?: boolean;
}

interface UseUniversePlansResult {
    plans: PickerPlan[];
    isPending: boolean;
    isFetching: boolean;
    isError: boolean;
}

const useUniversePlans = ({
    dim1Labels,
    dim2Labels,
    search,
    exclude,
    enabled = true,
}: UseUniversePlansArgs): UseUniversePlansResult => {
    const isEnabled = enabled && dim1Labels.length > 0 && dim2Labels.length > 0;

    const { data, isPending, isFetching, isError } = useQuery({
        queryKey: tpoGenKeys.universePlans(dim1Labels, dim2Labels, search, exclude),
        queryFn: ({ signal }) => fetchUniversePlans(dim1Labels, dim2Labels, search, exclude, signal),
        enabled: isEnabled,
    });

    return {
        plans: data?.data ?? [],
        isPending: isEnabled && isPending,
        isFetching,
        isError,
    };
};

export default useUniversePlans;
